import React, { useRef, useState } from "react";

import {
  AccountBalance,
  CalendarMonth,
  CameraAlt,
  CheckCircle,
  DescriptionOutlined,
  ImageOutlined,
  LockOutlined,
  Payments,
  ReceiptLong,
  UploadFile,
} from "@mui/icons-material";

import "./ChequePayment.css";

const ChequePayment = ({
  amount = 0,
  chequePayment,
  setChequePayment,
  executeChequePay,
}) => {
  const fileInputRef = useRef(null);

  const [isSubmitting, setSubmitting] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);

  // ADDED: Validation errors
  const [validationErrors, setValidationErrors] = useState({});

  const updateField = (field, value) => {
    setChequePayment((previous) => ({
      ...previous,
      [field]: value,
    }));

    // ADDED: Clear the error for this field when the user changes it
    setValidationErrors((previous) => {
      if (!previous[field]) return previous;

      const nextErrors = { ...previous };
      delete nextErrors[field];

      return nextErrors;
    });
  };

  // ADDED: Form validation
  const validateForm = () => {
    const errors = {};

    const bankName = chequePayment?.bankName?.trim();
    const chequeNumber = chequePayment?.chequeNumber?.trim();
    const chequeDate = chequePayment?.chequeDate?.trim();
    const payee = chequePayment?.payee?.trim();
    const amountInWords = chequePayment?.amountInWords?.trim();
    const accountName = chequePayment?.accountName?.trim();
    const branchName = chequePayment?.branchName?.trim();

    // Bank name
    if (!bankName) {
      errors.bankName = "Bank name is required.";
    }

    // Cheque number
    if (!chequeNumber) {
      errors.chequeNumber = "Cheque number is required.";
    } else if (!/^\d+$/.test(chequeNumber)) {
      errors.chequeNumber = "Cheque number must contain numbers only.";
    }

    // Cheque date
    if (!chequeDate) {
      errors.chequeDate = "Cheque date is required.";
    } else {
      const selectedDate = new Date(`${chequeDate}T00:00:00`);

      if (Number.isNaN(selectedDate.getTime())) {
        errors.chequeDate = "Enter a valid cheque date.";
      } else {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (selectedDate > today) {
          errors.chequeDate = "Cheque date cannot be in the future.";
        }
      }
    }

    // Payee
    if (!payee) {
      errors.payee = "Payee name is required.";
    }

    // Amount in words
    if (!amountInWords) {
      errors.amountInWords = "Amount in words is required.";
    }

    // Account holder
    if (!accountName) {
      errors.accountName = "Account holder name is required.";
    }

    // Branch
    if (!branchName) {
      errors.branchName = "Bank branch is required.";
    }

    setValidationErrors(errors);

    return Object.keys(errors).length === 0;
  };

  // ADDED: Automatically add error class when field is invalid
  const getInputClassName = (baseClass, field) => {
    return validationErrors[field]
      ? `${baseClass} chequeInputError`
      : baseClass;
  };

  // ADDED: Display validation message
  const renderValidationError = (field) => {
    if (!validationErrors[field]) return null;

    return (
      <span className="fs1 chequeValidationError">
        {validationErrors[field]}
      </span>
    );
  };

  const formatAmount = (value) => {
    const numericAmount = Number(value || 0);

    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericAmount);
  };

  const handleChequeImage = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    updateField("chequeImage", file);

    const imageUrl = URL.createObjectURL(file);

    setPreviewImage(imageUrl);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    // ADDED: Validate before submitting
    const isValid = validateForm();

    if (!isValid) return;

    setSubmitting(true);

    try {
      if (typeof executeChequePay === "function") {
        await executeChequePay();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="chequePaymentWrapps">
      <div
        className="chequePayment fx-cl space2 relative"
        onClick={(event) => event.stopPropagation()}
      >
        {/* ============================================================
            HEADER
        ============================================================ */}

        <div className="chequePaymentHeader fx-ac fx-jb">
          <div className="fx-ac space1">
            <div className="chequePaymentHeaderIcon fx-ac fx-jc">
              <Payments />
            </div>

            <div className="fx-cl spacem">
              <span className="fs4 fw600">
                Cheque Payment
              </span>

              <span className="fs1 cb">
                Record payment details from the customer's bank cheque.
              </span>
            </div>
          </div>

          <div className="chequePaymentStatus fx-ac space">
            <ReceiptLong />
            <span>CHEQUE</span>
          </div>
        </div>

        {/* ============================================================
            CHEQUE
        ============================================================ */}

        <div className="chequePaper shadowA relative">

          <div className="chequeWatermark">
            PAYMENT
          </div>

          <div className="chequeSecurityLine" />

          {/* ------------------------------------------------------------
              CHEQUE TOP
          ------------------------------------------------------------ */}

          <div className="chequeTop fx-jb">

            <div className="chequeBankArea fx-ac space1">

              <div className="chequeBankLogo fx-ac fx-jc">
                <AccountBalance />
              </div>

              <div className="fx-cl spacem">

                <span className="fs5 fwb">
                  {chequePayment?.bankName || "BANK NAME"}
                </span>

                <span className="fs1 cb">
                  {chequePayment?.branchName || "Bank Branch"}
                </span>

              </div>

            </div>

            <div className="chequeNumberArea fx-cl">

              <span className="chequeSmallLabel">
                CHEQUE NO.
              </span>

              <span className="chequeNumber fs4 fw600">
                {chequePayment?.chequeNumber || "000000"}
              </span>

            </div>

          </div>

          {/* ------------------------------------------------------------
              CHEQUE DATE
          ------------------------------------------------------------ */}

<div className="fx-jb">
    <span>&nbsp;</span>
    <div className="fx-cl spacem">
          <div className="chequeDateArea fx-je fx-ac space">

            <CalendarMonth />

            <span className="chequeSmallLabel">
              DATE
            </span>

            <input
              type="date"
              className={getInputClassName(
                "chequeDateInput",
                "chequeDate"
              )}
              value={chequePayment?.chequeDate || ""}
              onChange={(event) =>
                updateField("chequeDate", event.target.value)
              }
            /> 
          </div>
    {renderValidationError("chequeDate")}
</div>
</div>

          {/* ------------------------------------------------------------
              PAY TO
          ------------------------------------------------------------ */}

          <div className="chequePayeeArea">

            <div className="chequeFieldLabel">
              PAY TO THE ORDER OF
            </div>

            <input
              type="text"
              className={getInputClassName(
                "chequeLineInput chequePayeeInput",
                "payee"
              )}
              value={chequePayment?.payee || ""}
              onChange={(event) =>
                updateField("payee", event.target.value)
              }
              placeholder="Customer / Business name"
            />

            {renderValidationError("payee")}

          </div>

          {/* ------------------------------------------------------------
              AMOUNT
          ------------------------------------------------------------ */}

          <div className="chequeAmountRow fx-ac fx-jb">

            <div className="chequeAmountWords">

              <div className="chequeFieldLabel">
                AMOUNT IN WORDS
              </div>

              <input
                type="text"
                className={getInputClassName(
                  "chequeLineInput",
                  "amountInWords"
                )}
                value={chequePayment?.amountInWords || ""}
                onChange={(event) =>
                  updateField(
                    "amountInWords",
                    event.target.value
                  )
                }
                placeholder="Amount written in words"
              />

              {renderValidationError("amountInWords")}

            </div>

            <div className="chequeAmountBox fx-ac">

              <span className="chequeCurrency">
                ₦
              </span>

              <span className="chequeAmountValue">
                {formatAmount(amount)}
              </span>

            </div>

          </div>

          {/* ------------------------------------------------------------
              DRAWER / ACCOUNT HOLDER
          ------------------------------------------------------------ */}

          <div className="chequeBottomRow fx-ac fx-jb">

            <div className="chequeDrawerArea fx-cl">

              <span className="chequeSmallLabel">
                DRAWER / ACCOUNT HOLDER
              </span>

              <input
                type="text"
                className={getInputClassName(
                  "chequeLineInput",
                  "accountName"
                )}
                value={chequePayment?.accountName || ""}
                onChange={(event) =>
                  updateField(
                    "accountName",
                    event.target.value
                  )
                }
                placeholder="Name on cheque"
              />

              {renderValidationError("accountName")}

            </div>

            <div className="chequeSignatureArea fx-cl">

              <span className="chequeSmallLabel">
                AUTHORIZED SIGNATURE
              </span>

              <div className="chequeSignatureLine" />

            </div>

          </div>

          {/* ------------------------------------------------------------
              MICR / CHEQUE IDENTIFICATION
          ------------------------------------------------------------ */}

          <div className="chequeMicrArea fx-ac fx-jb">

            <span className="chequeMicrText">
              ⑆ {chequePayment?.chequeNumber || "000000"} ⑆
            </span>

            <span className="chequeMicrText">
              ⑆ {chequePayment?.branchName || "BRANCH"} ⑆
            </span>

            <span className="chequeMicrText">
              ⑆ ACCOUNT PAY ⑆
            </span>

          </div>

          <div className="chequeSecurityStrip">
            <span>
              VALID FOR PAYMENT • ORIGINAL CHEQUE REQUIRED
            </span>
          </div>

        </div>

        {/* ============================================================
            CHEQUE INFORMATION
        ============================================================ */}

        <div className="chequeInformationCard fx-cl space2">

          <div className="chequeSectionHeading fx-ac space">

            <div className="chequeSectionIcon fx-ac fx-jc">
              <AccountBalance />
            </div>

            <div className="fx-cl spacem">
              <span className="fs3 fw600">
                Cheque Information
              </span>

              <span className="fs1 cb">
                Verify the information exactly as shown on the cheque.
              </span>
            </div>

          </div>

          <div className="g2 chequeFormGrid">

            {/* BANK */}

            <div className="chequeFormField fx-cl space">

              <label className="fs2 fw600">
                Bank Name
              </label>

              <div
                className={getInputClassName(
                  "chequeInputWrapper fx-ac space",
                  "bankName"
                )}
              >

                <AccountBalance />

                <input
                  type="text"
                  value={chequePayment?.bankName || ""}
                  onChange={(event) =>
                    updateField(
                      "bankName",
                      event.target.value
                    )
                  }
                  placeholder="Enter bank name"
                />

              </div>

              {renderValidationError("bankName")}

            </div>

            {/* CHEQUE NUMBER */}

            <div className="chequeFormField fx-cl space">

              <label className="fs2 fw600">
                Cheque Number
              </label>

              <div
                className={getInputClassName(
                  "chequeInputWrapper fx-ac space",
                  "chequeNumber"
                )}
              >

                <ReceiptLong />

                <input
                  type="text"
                  value={chequePayment?.chequeNumber || ""}
                  onChange={(event) =>
                    updateField(
                      "chequeNumber",
                      event.target.value
                    )
                  }
                  placeholder="Enter cheque number"
                />

              </div>

              {renderValidationError("chequeNumber")}

            </div>

            {/* ACCOUNT HOLDER */}

            <div className="chequeFormField fx-cl space">

              <label className="fs2 fw600">
                Account Holder
              </label>

              <input
                type="text"
                className={getInputClassName(
                  "chequeStandardInput",
                  "accountName"
                )}
                value={chequePayment?.accountName || ""}
                onChange={(event) =>
                  updateField(
                    "accountName",
                    event.target.value
                  )
                }
                placeholder="Name on the cheque"
              />

              {renderValidationError("accountName")}

            </div>

            {/* BRANCH */}

            <div className="chequeFormField fx-cl space">

              <label className="fs2 fw600">
                Bank Branch
              </label>

              <input
                type="text"
                className={getInputClassName(
                  "chequeStandardInput",
                  "branchName"
                )}
                value={chequePayment?.branchName || ""}
                onChange={(event) =>
                  updateField(
                    "branchName",
                    event.target.value
                  )
                }
                placeholder="Enter branch name"
              />

              {renderValidationError("branchName")}

            </div>

          </div>

        </div>

        {/* ============================================================
            CHEQUE IMAGE
        ============================================================ */}

        <div className="chequeAttachmentCard fx-cl space1">

          <div className="fx-ac fx-jb">

            <div className="fx-ac space">

              <div className="chequeAttachmentIcon fx-ac fx-jc">
                <ImageOutlined />
              </div>

              <div className="fx-cl spacem">

                <span className="fs2 fw600">
                  Cheque Image
                </span>

                <span className="fs1 cb">
                  Optional — keep a copy for transaction records.
                </span>

              </div>

            </div>

            <button
              type="button"
              className="chequeUploadButton fx-ac space"
              onClick={() => fileInputRef.current?.click()}
            >
              <UploadFile />
              <span>Upload</span>
            </button>

          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleChequeImage}
          />

          {previewImage && (
            <div className="chequeImagePreview relative">

              <img
                src={previewImage}
                alt="Cheque preview"
              />

              <button
                type="button"
                className="chequeRemoveImage"
                onClick={() => {
                  setPreviewImage(null);
                  updateField("chequeImage", null);

                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Remove
              </button>

            </div>
          )}

        </div>

        {/* ============================================================
            DESCRIPTION
        ============================================================ */}

        <div className="chequeDescription fx-cl space">

          <div className="fx-ac space">

            <DescriptionOutlined className="chequeDescriptionIcon" />

            <label
              htmlFor="cheque-description"
              className="fs2 fw600"
            >
              Transaction Description
            </label>

            <span className="fs1 cb">
              (Optional)
            </span>

          </div>

          <textarea
            id="cheque-description"
            className="chequeDescriptionInput"
            rows={3}
            maxLength={250}
            value={chequePayment?.description || ""}
            onChange={(event) =>
              updateField(
                "description",
                event.target.value
              )
            }
            placeholder="Enter description for this cheque transaction, if applicable..."
          />

          <div className="fx-je">

            <span className="fs1 cb">
              {(chequePayment?.description || "").length}/250
            </span>

          </div>

        </div>

        {/* ============================================================
            PAYMENT STATUS
        ============================================================ */}

        <div className="chequePendingNotice fx-ac space1">

          <div className="chequePendingIcon fx-ac fx-jc">
            <CheckCircle />
          </div>

          <div className="fx-cl spacem">

            <span className="fs2 fw600">
              Payment will be recorded as pending clearance
            </span>

            <span className="fs1 cb">
              The sale can be recorded, but the cheque should be
              verified and cleared before treating the funds as
              settled.
            </span>

          </div>

        </div>

        {/* ============================================================
            SUBMIT
        ============================================================ */}

        <button
          type="button"
          className={
            isSubmitting
              ? "chequeSubmitButton chequeSubmitLoading fx-ac fx-jc space1"
              : "chequeSubmitButton fx-ac fx-jc space1"
          }
          disabled={isSubmitting}
          onClick={handleSubmit}
        >

          {isSubmitting ? (
            <>
              <span className="chequeSpinner" />
              <span>Processing Cheque...</span>
            </>
          ) : (
            <>
              <Payments />
              <span>Submit Cheque Payment</span>
            </>
          )}

        </button>

        {/* ============================================================
            FOOTER
        ============================================================ */}

        <div className="chequeFooter fx-ac fx-jc space">

          <LockOutlined />

          <span>
            Verify the cheque number, bank, date, amount and drawer
            details before submitting. Cheque payments may remain
            pending until the cheque has been successfully cleared.
          </span>

        </div>

      </div>
    </div>
  );
};

export default ChequePayment;